import { PageBackLink } from "@/components/page-back-link"
import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { MapPin, User } from 'lucide-react'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { PatientDeactivationControls } from '@/features/patients/components/patient-deactivation-controls'
import { SaveChangesDialog } from '@/features/patients/components/SaveChangesDialog'
import { usePortalAuth } from '@/features/authz/hooks/use-portal-auth'
import { buildPatientAddressDisplay } from '@/features/patients/utils/address-display'
import { PatientSaveError, savePatientChanges } from '@/features/patients/utils/save-patient-changes'
import {
	ADDRESS_CITY_MAX_LENGTH,
	ADDRESS_POSTAL_CODE_MAX_LENGTH,
	ADDRESS_REGION_MAX_LENGTH,
	ADDRESS_STREET_1_MAX_LENGTH,
	ADDRESS_STREET_2_MAX_LENGTH,
	type UpdatePatientProfile,
} from '@optimist/schemas'
import {
	useUpdatePatient,
	useUpdatePatientAddress,
} from '@/features/patients/hooks/usePatientMutations'
import {
	buildAddressUpdatePayloads,
	buildEditedAddressSnapshot,
	buildEditedProfileSnapshot,
	buildPatientEditFormValues,
	buildProfileUpdatePayload,
	hasPatientEditChanges,
	validatePatientEdit,
	type AddressUpdatePayload,
} from '@/features/patients/validation/patient-edit'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/utils'
import { PatientDetailState } from '@/features/patients/components/patient-detail-state'
import { PatientOrdersSection } from '@/features/patients/components/patient-orders-section'
import {
	canEditPatientProfile,
	getDisplayValue,
	getInputValue,
	getPatientPhiDisplayValue,
	type PatientAddress,
	type PatientData,
	type PatientDetailResponse,
} from '@/features/patients/patient-detail-model'


function FieldError(props: { readonly errors: Array<string | undefined> }) {
	const filtered = props.errors.filter((error): error is string => typeof error === "string")
	if (filtered.length === 0) {
		return null
	}
	return <p className="text-meta text-destructive">{filtered[0]}</p>
}

/**
 * Patient Detail page
 * Purpose: Contextual view only
 * No workflow actions here
 */
export const Route = createFileRoute('/dashboard/patients/$patientId')({
	component: PatientDetailPage,
})

function PatientDetailPage() {
	const { patientId } = Route.useParams()
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: queryKeys.patients.detail(patientId),
		queryFn: () => api.get<PatientDetailResponse>(`/patients/${patientId}`),
	})
	const authQuery = usePortalAuth()

	const [isEditing, setIsEditing] = useState(false)
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [pendingProfilePayload, setPendingProfilePayload] = useState<UpdatePatientProfile>({})
	const [pendingAddressPayloads, setPendingAddressPayloads] = useState<AddressUpdatePayload[]>([])
	const [pendingEditedProfile, setPendingEditedProfile] = useState<Partial<PatientData>>({})
	const [pendingEditedAddresses, setPendingEditedAddresses] = useState<Record<string, Partial<PatientAddress>>>({})

	const updatePatientMutation = useUpdatePatient(patientId)
	const updateAddressMutation = useUpdatePatientAddress(patientId)

	const patient = data ? data.patient : null
	const addresses = data ? data.addresses : []
	const orders = data ? data.orders : []
	const deactivationRequest = data ? data.deactivationRequest : null
	const deactivationImpact = data ? data.deactivationImpact : null
	const permissions: readonly string[] = authQuery.data === undefined ? [] : authQuery.data.permissions
	const canManagePatient = permissions.includes("patients.manage")
	const canReadPatientPhi = permissions.includes("patients.read.phi")
	const canEditPatient = canEditPatientProfile(permissions)
	const canApproveDeactivation = canManagePatient && permissions.includes("patients.deactivate")
	const currentUserId = authQuery.data === undefined ? "" : authQuery.data.userId
	const isPatientEditActive = isEditing && canEditPatient

	const defaultValues = buildPatientEditFormValues(patient, addresses)
	const form = useForm({
		defaultValues,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const validation = validatePatientEdit(value)
				if (!validation.ok) {
					return {
						form: validation.formError,
						fields: validation.fieldErrors,
					}
				}
				return null
			},
		},
		onSubmit: ({ value }) => {
			if (!patient || !canEditPatient) {
				return
			}

			const profilePayload = buildProfileUpdatePayload({
				original: patient,
				form: value.profile,
			})

			const addressUpdates = buildAddressUpdatePayloads({
				originalAddresses: addresses,
				formAddresses: value.addresses,
			})

			const editedProfile = buildEditedProfileSnapshot(value.profile)
			const editedAddresses = buildEditedAddressSnapshot(value.addresses)

			setPendingProfilePayload(profilePayload)
			setPendingAddressPayloads(addressUpdates)
			setPendingEditedProfile(editedProfile)
			setPendingEditedAddresses(editedAddresses)
			setIsConfirmOpen(true)
		},
	})

	const handleRetry = () => {
		void refetch()
	}

	if (isLoading || error !== null || patient === null) {
		return <PatientDetailState patientId={patientId} isLoading={isLoading} error={error} onRetry={handleRetry} canEdit={canEditPatient} canManage={canManagePatient} />
	}

	const startEditing = () => {
		if (!canEditPatient) {
			return
		}
		const nextValues = buildPatientEditFormValues(patient, addresses)
		form.reset(nextValues)
		setIsEditing(true)
	}

	const cancelEditing = () => {
		const nextValues = buildPatientEditFormValues(patient, addresses)
		form.reset(nextValues)
		setIsEditing(false)
		setIsConfirmOpen(false)
		setPendingProfilePayload({})
		setPendingAddressPayloads([])
		setPendingEditedProfile({})
		setPendingEditedAddresses({})
	}

	const handleConfirmOpenChange = (open: boolean) => {
		setIsConfirmOpen(open)
		if (!open) {
			setPendingProfilePayload({})
			setPendingAddressPayloads([])
			setPendingEditedProfile({})
			setPendingEditedAddresses({})
		}
	}

	const fullNameParts: string[] = []
	if (patient.firstName) fullNameParts.push(patient.firstName)
	if (patient.lastName) fullNameParts.push(patient.lastName)
	const fullName = fullNameParts.length > 0 ? fullNameParts.join(' ') : 'Unknown'

	const originalProfileForDialog: {
		firstName?: string
		lastName?: string
		dateOfBirth?: string
		gender?: string
	} = {}
	if (patient.firstName !== undefined) {
		originalProfileForDialog.firstName = patient.firstName
	}
	if (patient.lastName !== undefined) {
		originalProfileForDialog.lastName = patient.lastName
	}
	if (patient.gender !== undefined) {
		originalProfileForDialog.gender = patient.gender
	}
	if (patient.dateOfBirth !== undefined) {
		originalProfileForDialog.dateOfBirth = patient.dateOfBirth
	}

	const isSaving = updatePatientMutation.isPending || updateAddressMutation.isPending
	const handleConfirmSave = async () => {
		const updates: Array<() => Promise<unknown>> = []
		if (Object.keys(pendingProfilePayload).length > 0) {
			updates.push(() => updatePatientMutation.mutateAsync(pendingProfilePayload))
		}
		for (const update of pendingAddressPayloads) {
			updates.push(() => updateAddressMutation.mutateAsync({
				addressId: update.addressId,
				payload: update.payload,
			}))
		}
		try {
			await savePatientChanges(updates)
		} catch (error) {
			if (error instanceof PatientSaveError && error.permissionDenied) await authQuery.refetch()
			throw error
		}

		setIsEditing(false)
	}

	return (
        <ErrorBoundary
			title="Patient detail error"
			fallback={<ErrorBanner title="Patient unavailable" message="Something went wrong loading this page." />}
		>
            <div className="space-y-6">
				<div>
					<PageBreadcrumbs
						items={[
							<Link key="dashboard" to="/dashboard">Dashboard</Link>,
							<Link key="patients" to="/dashboard/patients" search={{ page: 1, limit: 25 }}>Patients</Link>,
							<span key="patient">{fullName}</span>,
						]}
					/>
				</div>

				<div className="flex flex-col gap-5 py-2 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<p className="font-inter text-label font-semibold leading-[1.2] tracking-[0.16em] uppercase text-brand">Patient record</p>
						<div className="mt-2 flex items-center gap-3">
							<PageBackLink to="/dashboard/patients" search={{ page: 1, limit: 25 }} label="Back to patients" />
							<h1 className="font-avenir text-page-title font-semibold text-heading">{fullName}</h1>
						</div>
						<p className="mt-2 text-meta font-medium text-muted-foreground">
							{patient.universalPatientId}
							<span className="px-1.5 text-separator">•</span>
							Created {formatDateTime(patient.createdAt)}
						</p>
					</div>
					<div className="flex items-center gap-2">
						{isPatientEditActive ? (
							<>
								<Button variant="outline" onClick={cancelEditing} disabled={isSaving}>
									Cancel
								</Button>
								<form.Subscribe selector={(state) => state.values}>
										{(formValues) => {
										const hasChanges = hasPatientEditChanges({
											originalProfile: patient,
											originalAddresses: addresses,
											formValues,
										})
										return (
											<Button aria-busy={isSaving}
												onClick={() => {
													void form.handleSubmit()
												}}
												disabled={!hasChanges || isSaving}
											>
												Save changes
											</Button>
										)
									}}
								</form.Subscribe>
							</>
						) : (
							<>
								{canEditPatient ? (
									<Button variant="secondary"
										onClick={startEditing}
									>
										Edit
									</Button>
								) : null}
							</>
						)}
					</div>
				</div>

				<SaveChangesDialog
					open={isConfirmOpen}
					onOpenChange={handleConfirmOpenChange}
					originalProfile={originalProfileForDialog}
					editedProfile={pendingEditedProfile}
					originalAddresses={addresses}
					editedAddresses={pendingEditedAddresses}
					onConfirm={handleConfirmSave}
					isPending={isSaving}
					canConfirm={canEditPatient}
				/>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="lg:col-span-2 space-y-6">
						<div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
							<div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
								<div className="flex items-center gap-3">
									<div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
										<User size={18} strokeWidth={1.8} />
									</div>
									<div>
										<h3 className="text-section-title font-semibold text-heading">Profile</h3>
										<p className="mt-0.5 text-body text-muted-foreground">Patient identity and contact details.</p>
									</div>
								</div>
							</div>
							<div className="p-6">
								{isPatientEditActive ? (
									<form.Subscribe selector={(state) => state.errorMap}>
										{(errorMap) =>
											errorMap.onSubmit ? (
												<div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-meta text-destructive mb-4">
													{errorMap.onSubmit}
												</div>
											) : null
										}
									</form.Subscribe>
								) : null}
								<div className="rounded-xl border border-border-subtle bg-muted p-4">
									<div className="grid grid-cols-2 gap-4 [&>div]:min-w-0">
										<div>
											<p className="font-inter text-data text-quiet-foreground">
												First Name
											</p>
										{isPatientEditActive ? (
											<form.Field name="profile.firstName">
												{(field) => (
													<div className="space-y-2">
														<Input
															id={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(event) => field.handleChange(event.target.value)}
														/>
														<FieldError errors={field.state.meta.errors} />
													</div>
												)}
											</form.Field>
										) : (
											<p className="font-inter text-body font-medium text-foreground">
												{getDisplayValue(patient.firstName)}
											</p>
										)}
									</div>
									<div>
										<p className="font-inter text-data text-quiet-foreground">
											Last Name
										</p>
										{isPatientEditActive ? (
											<form.Field name="profile.lastName">
												{(field) => (
													<div className="space-y-2">
														<Input
															id={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(event) => field.handleChange(event.target.value)}
														/>
														<FieldError errors={field.state.meta.errors} />
													</div>
												)}
											</form.Field>
										) : (
											<p className="font-inter text-body font-medium text-foreground">
												{getDisplayValue(patient.lastName)}
											</p>
										)}
									</div>
									<div>
										<p className="font-inter text-data text-quiet-foreground">
											Date of Birth
										</p>
										{isPatientEditActive ? (
											<form.Field name="profile.dateOfBirth">
												{(field) => (
													<div className="space-y-2">
														<Input
															id={field.name}
															type="date"
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(event) => field.handleChange(event.target.value)}
														/>
														<FieldError errors={field.state.meta.errors} />
													</div>
												)}
											</form.Field>
										) : (
											<p className="font-inter text-body font-medium text-foreground">
												{getPatientPhiDisplayValue({
													canReadPatientPhi,
													value: patient.dateOfBirth,
													format: formatDate,
												})}
											</p>
										)}
									</div>
									<div>
										<p className="font-inter text-data text-quiet-foreground">
											Gender
										</p>
										{isPatientEditActive ? (
											<form.Field name="profile.gender">
												{(field) => (
													<div className="space-y-2">
														<Input
															id={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(event) => field.handleChange(event.target.value)}
														/>
														<FieldError errors={field.state.meta.errors} />
													</div>
												)}
											</form.Field>
										) : (
											<p className="font-inter text-body font-medium capitalize text-foreground">
												{getPatientPhiDisplayValue({ canReadPatientPhi, value: patient.gender })}
											</p>
										)}
									</div>
									<div>
										<div className="flex items-center gap-2">
											<p className="font-inter text-data text-quiet-foreground">
												Email
											</p>
											{isPatientEditActive ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="cursor-help text-meta text-quiet-foreground underline">Locked</span>
													</TooltipTrigger>
													<TooltipContent>Email is managed by Cognito and cannot be edited.</TooltipContent>
												</Tooltip>
											) : null}
										</div>
										{isPatientEditActive ? (
											<Input value={getInputValue(patient.email)} disabled />
										) : (
											<p className="truncate font-inter text-body font-medium text-foreground" title={getPatientPhiDisplayValue({ canReadPatientPhi, value: patient.email })}>
												{getPatientPhiDisplayValue({ canReadPatientPhi, value: patient.email })}
											</p>
										)}
									</div>
									<div>
										<div className="flex items-center gap-2">
											<p className="font-inter text-data text-quiet-foreground">
												Phone
											</p>
											{isPatientEditActive ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="cursor-help text-meta text-quiet-foreground underline">Locked</span>
													</TooltipTrigger>
													<TooltipContent>Phone is managed by Cognito and cannot be edited.</TooltipContent>
												</Tooltip>
											) : null}
										</div>
										{isPatientEditActive ? (
											<Input value={getInputValue(patient.phone)} disabled />
										) : (
											<p className="font-inter text-body font-medium text-foreground">
												{getPatientPhiDisplayValue({ canReadPatientPhi, value: patient.phone })}
											</p>
										)}
									</div>
									<div>
										<p className="font-inter text-data text-quiet-foreground">
											Created
										</p>
										<p className="font-inter text-body font-medium text-foreground">
											{formatDateTime(patient.createdAt)}
										</p>
									</div>
								</div>
							</div>
							</div>
							<PatientDeactivationControls
								patientId={patientId}
								universalPatientId={patient.universalPatientId}
								deactivatedAt={patient.deactivatedAt}
								request={deactivationRequest}
								impact={deactivationImpact}
								currentUserId={currentUserId}
								canManage={canManagePatient}
								canApprove={canApproveDeactivation}
							/>
						</div>

						<div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
							<div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
								<div className="flex items-center gap-3">
									<div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
										<MapPin size={18} strokeWidth={1.8} />
									</div>
									<div>
										<h3 className="text-section-title font-semibold text-heading">Addresses</h3>
										<p className="mt-0.5 text-body text-muted-foreground">Saved shipping and billing addresses.</p>
									</div>
								</div>
							</div>
							<div className="p-6">
								{!canReadPatientPhi ? (
									<div className="rounded-xl border border-border-subtle bg-muted p-4">
										<p className="font-inter text-body font-medium text-foreground">Restricted</p>
										<p className="mt-1 font-inter text-meta text-muted-foreground">
											Address details require patient PHI access.
										</p>
									</div>
								) : addresses.length === 0 ? (
									<p className="font-inter text-data text-quiet-foreground">
										No addresses on file
									</p>
								) : (
									<div className="space-y-3">
										{addresses.map((addr, index) => {
											const display = buildPatientAddressDisplay(addr, true)
											return (
												<div
													key={addr.id}
													className="rounded-xl border border-border-subtle bg-muted p-4"
												>
													<div className="mb-2 flex items-center justify-between">
														<span className="font-inter text-meta font-semibold tracking-[0.5px] text-quiet-foreground uppercase">
															{addr.type}
														</span>
														{addr.isDefault ? (
															<StatusBadge variant="info">Default</StatusBadge>
														) : null}
													</div>
													{isPatientEditActive ? (
														<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
													<div>
														<p className="text-meta text-muted-foreground">Street 1</p>
														<form.Field name={`addresses[${index}].street1`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={ADDRESS_STREET_1_MAX_LENGTH}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
													<div>
														<p className="text-meta text-muted-foreground">Street 2</p>
														<form.Field name={`addresses[${index}].street2`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={ADDRESS_STREET_2_MAX_LENGTH}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
													<div>
														<p className="text-meta text-muted-foreground">City</p>
														<form.Field name={`addresses[${index}].city`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={ADDRESS_CITY_MAX_LENGTH}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
													<div>
														<p className="text-meta text-muted-foreground">Region</p>
														<form.Field name={`addresses[${index}].region`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={ADDRESS_REGION_MAX_LENGTH}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
													<div>
														<p className="text-meta text-muted-foreground">Postal code</p>
														<form.Field name={`addresses[${index}].postalCode`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={ADDRESS_POSTAL_CODE_MAX_LENGTH}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
													<div>
														<p className="text-meta text-muted-foreground">Country</p>
														<form.Field name={`addresses[${index}].countryCode`}>
															{(field) => (
																<div className="space-y-2">
																	<Input
																		id={field.name}
																		maxLength={2}
																		value={field.state.value}
																		onBlur={field.handleBlur}
																		onChange={(event) => field.handleChange(event.target.value)}
																	/>
																	<FieldError errors={field.state.meta.errors} />
																</div>
															)}
														</form.Field>
													</div>
														</div>
													) : (
														<>
															<p className="font-inter text-body font-medium text-foreground">
																{addr.label}
															</p>
															<p className="font-inter text-body font-medium text-foreground">
																{display.primaryLine}
															</p>
															{display.secondaryLine ? (
																<p className="font-inter text-body font-medium text-foreground">
																	{display.secondaryLine}
																</p>
															) : null}
															{display.tertiaryLine ? (
																<p className="font-inter text-body font-medium text-foreground">
																	{display.tertiaryLine}
																</p>
															) : null}
															<p className="font-inter text-data text-quiet-foreground">
																{addr.countryCode}
															</p>
														</>
													)}
												</div>
											)
										})}
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="space-y-6">
					<PatientOrdersSection orders={orders} />
					</div>
				</div>
			</div>
        </ErrorBoundary>
    )
}
